import SearchOutlined from '@mui/icons-material/SearchOutlined'
import { Box, Fade, InputAdornment, LinearProgress, Stack, TextField } from '@mui/material'
import { addDays, addMonths, endOfMonth, startOfMonth, subDays } from 'date-fns'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { prefetchGlobalCalendar, useGetGlobalCalendar } from 'src/api/calendar'
import GlobalCalendar, { useCalendarTypeStyle } from 'src/components/calendar/GlobalCalendar'
import { CalendarSurface, CalendarToolbar } from 'src/components/calendar/GlobalCalendar.style'
import { CalendarEventType, ICalendarEvent } from 'src/generated-types/calendarEvent'
import { getFormattedDate } from 'src/utils'
import { AppRoutesNavigation } from '../../../app-routes'
import { ALL_TYPES, CalendarEventTypes, CalendarPopoverState, typeLabelKey } from './calendar.constants'
import EventPreviewPopover from './EventPreviewPopover'
import { FilterChip, FilterChipDot } from './GlobalCalendarContent.style'
import ServiceFrequencyFilter from './ServiceFrequencyFilter'

interface GlobalCalendarContentProps {
  customerId?: EntityId
  onNavigate?: () => void
}

const GlobalCalendarContent = ({ customerId, onNavigate }: GlobalCalendarContentProps) => {
  const { t } = useTranslation(['calendar', 'common'])
  const navigate = useNavigate()
  const getTypeStyle = useCalendarTypeStyle()

  const [monthDate, setMonthDate] = useState<Date>(new Date())
  const [selectedTypes, setSelectedTypes] = useState<CalendarEventType[]>(ALL_TYPES)
  const [serviceFrequencyIds, setServiceFrequencyIds] = useState<number[]>([])
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [popover, setPopover] = useState<CalendarPopoverState | null>(null)
  const servicesActive = selectedTypes.includes(CalendarEventTypes.Service)

  useEffect(() => {
    const handle = setTimeout(() => setSearch(searchInput.trim()), 300)
    return () => clearTimeout(handle)
  }, [searchInput])

  // Cover the visible month grid (which bleeds into adjacent months) with a 7-day buffer.
  const from = useMemo(() => getFormattedDate(subDays(startOfMonth(monthDate), 7)), [monthDate])
  const to = useMemo(() => getFormattedDate(addDays(endOfMonth(monthDate), 7)), [monthDate])

  const { data: events = [], isFetching } = useGetGlobalCalendar(customerId as EntityId, {
    from,
    to,
    types: selectedTypes.length === ALL_TYPES.length ? undefined : selectedTypes.join(','),
    search: search || undefined,
  })

  // Frequency options are derived from the loaded service events (the /calendar endpoint already
  // returns frequencyId/Name), avoiding the facility-permission-gated /services call. Filtering is
  // applied client-side so the option list stays stable while a filter is active.
  const frequencyOptions = useMemo(() => {
    const byId = new Map<number, string>()
    events.forEach(e => {
      if (e.type === CalendarEventTypes.Service && e.frequencyId != null) byId.set(e.frequencyId, e.frequencyName ?? '')
    })
    return Array.from(byId, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name))
  }, [events])

  const displayedEvents = useMemo(() => {
    if (!servicesActive || serviceFrequencyIds.length === 0) return events
    return events.filter(
      e =>
        e.type !== CalendarEventTypes.Service || (e.frequencyId != null && serviceFrequencyIds.includes(e.frequencyId)),
    )
  }, [events, servicesActive, serviceFrequencyIds])

  // Once the current month has loaded, warm the next month so paging forward is instant.
  useEffect(() => {
    if (!customerId || isFetching) return
    const next = addMonths(monthDate, 1)
    prefetchGlobalCalendar(customerId, {
      from: getFormattedDate(subDays(startOfMonth(next), 7)),
      to: getFormattedDate(addDays(endOfMonth(next), 7)),
      types: selectedTypes.length === ALL_TYPES.length ? undefined : selectedTypes.join(','),
      search: search || undefined,
    })
  }, [customerId, isFetching, monthDate, selectedTypes, search])

  const toggleType = (type: CalendarEventType) => {
    setSelectedTypes(prev => (prev.includes(type) ? prev.filter(x => x !== type) : [...prev, type]))
  }

  const buildHref = (event: ICalendarEvent): string | undefined => {
    // Projected (not-yet-persisted) occurrences have no detail page to open.
    if (event.isProjected || !event.localityId || !customerId) return undefined
    switch (event.type) {
      case CalendarEventTypes.Deviation:
        return AppRoutesNavigation.Localities.deviation(customerId, event.localityId, event.entityId)
      case CalendarEventTypes.Document:
        return AppRoutesNavigation.Localities.document(customerId, event.localityId, event.entityId)
      case CalendarEventTypes.Service:
        return AppRoutesNavigation.Localities.localityService(customerId, event.localityId, event.entityId)
      case CalendarEventTypes.WorkOrder:
        return AppRoutesNavigation.Localities.workOrder(customerId, event.localityId, event.entityId)
      default:
        return undefined
    }
  }

  const handleSelectEvent = (event: ICalendarEvent, mouseEvent?: React.MouseEvent | MouseEvent) => {
    if (mouseEvent && (mouseEvent.ctrlKey || mouseEvent.metaKey)) {
      const href = buildHref(event)
      if (href) window.open(href, '_blank')
      return
    }
    const top = mouseEvent?.clientY ?? window.innerHeight / 2
    const left = mouseEvent?.clientX ?? window.innerWidth / 2
    setPopover({ event, position: { top, left } })
  }

  const openEvent = (event: ICalendarEvent, newTab = false) => {
    const href = buildHref(event)
    if (!href) return
    if (newTab) window.open(href, '_blank')
    else {
      navigate(href)
      onNavigate?.()
    }
    setPopover(null)
  }

  return (
    <Stack spacing={2} sx={{ width: '100%' }}>
      {/* Toolbar: type filters (left) — service frequency + search (right) */}
      <CalendarToolbar elevation={0}>
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
          {ALL_TYPES.map(type => {
            const active = selectedTypes.includes(type)
            const { main, text, tint } = getTypeStyle(type)
            return (
              <FilterChip
                key={type}
                label={t(typeLabelKey[type], { ns: 'calendar' })}
                onClick={() => toggleType(type)}
                icon={<FilterChipDot component="span" main={main} active={active} />}
                active={active}
                main={main}
                text={text}
                tint={tint}
              />
            )
          })}
        </Stack>
        <Box sx={{ flex: 1 }} />
        {servicesActive && (
          <ServiceFrequencyFilter
            options={frequencyOptions}
            value={serviceFrequencyIds}
            onChange={setServiceFrequencyIds}
          />
        )}
        <TextField
          size="small"
          placeholder={t('search-placeholder', { ns: 'calendar' })}
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          sx={{ minWidth: 260, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchOutlined fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </CalendarToolbar>

      {/* Calendar */}
      <CalendarSurface elevation={0}>
        <Fade in={isFetching} unmountOnExit>
          <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, zIndex: 2 }} />
        </Fade>
        <Box
          sx={{
            opacity: isFetching ? 0.55 : 1,
            transition: 'opacity .2s ease',
            pointerEvents: isFetching ? 'none' : 'auto',
          }}
        >
          <GlobalCalendar events={displayedEvents} onSelectEvent={handleSelectEvent} onMonthChange={setMonthDate} />
        </Box>
      </CalendarSurface>

      <EventPreviewPopover
        state={popover}
        onClose={() => setPopover(null)}
        onOpen={openEvent}
        canOpen={event => !!buildHref(event)}
      />
    </Stack>
  )
}

export default GlobalCalendarContent
